import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Cube, Link, ShieldCheck, Users, ArrowsClockwise, Lightning, Lock, Globe, Database, ArrowRight, CheckCircle, CaretDown, CaretRight
} from "@phosphor-icons/react";

const SECTIONS = [
  {
    id: "what",
    title: "What is Blockchain?",
    icon: Cube,
    color: "text-amber-400",
    content: [
      {
        heading: "A Digital Ledger",
        text: "A blockchain is a shared digital record book that stores information in blocks linked together in a chain. Once data is written, it cannot be changed or deleted.",
      },
      {
        heading: "Blocks + Chain",
        text: "Each block holds a batch of transactions. When a block is full, it's sealed and connected to the previous block using a unique code (hash). This creates an unbreakable chain of records.",
      },
      {
        heading: "No Central Authority",
        text: "Unlike a bank's database controlled by one company, a blockchain is maintained by thousands of computers (nodes) worldwide. No single person or company owns it.",
      },
      {
        heading: "Think of it like...",
        text: "A Google Doc that everyone can view, but nobody can edit past entries. Every change is recorded permanently, and everyone has the same copy.",
      },
    ],
    keyPoints: [
      "Distributed digital ledger shared across many computers",
      "Data stored in blocks, linked chronologically",
      "Immutable — once written, records can't be altered",
      "No single point of failure or control",
    ],
  },
  {
    id: "why",
    title: "Why Does Blockchain Matter?",
    icon: Lightning,
    color: "text-emerald-400",
    content: [
      {
        heading: "Trust Without Middlemen",
        text: "You can send money, sign contracts, or prove ownership without needing a bank, lawyer, or notary. The blockchain itself is the trust layer.",
      },
      {
        heading: "Transparency",
        text: "Every transaction is visible to everyone on the network. This makes fraud extremely difficult because anyone can verify the records.",
      },
      {
        heading: "Security",
        text: "Blockchain uses advanced cryptography. To hack it, you'd need to simultaneously control over 50% of all computers on the network — practically impossible on major chains.",
      },
      {
        heading: "Financial Access",
        text: "2 billion people worldwide don't have bank accounts. Blockchain lets anyone with a phone send, receive, and store value without permission from institutions.",
      },
      {
        heading: "Programmable Money",
        text: "Smart contracts are programs that run on blockchain. They automatically execute when conditions are met — no paperwork, no delays, no middlemen taking fees.",
      },
    ],
    keyPoints: [
      "Removes need for trusted intermediaries (banks, brokers)",
      "Complete transparency — all transactions are publicly verifiable",
      "Near-impossible to hack or forge records",
      "Enables financial services for the unbanked",
      "Automates agreements through smart contracts",
    ],
  },
  {
    id: "how",
    title: "How Does Blockchain Work?",
    icon: ArrowsClockwise,
    color: "text-blue-400",
    content: [
      {
        heading: "Step 1: Transaction",
        text: "You initiate a transaction (e.g., send 1 BTC to someone). This is broadcast to the network.",
      },
      {
        heading: "Step 2: Verification",
        text: "Computers on the network (validators/miners) check that you have enough funds and the transaction is legitimate.",
      },
      {
        heading: "Step 3: Block Creation",
        text: "Verified transactions are grouped into a block. Each block contains a timestamp, transaction data, and the previous block's hash.",
      },
      {
        heading: "Step 4: Consensus",
        text: "The network agrees the block is valid using a consensus mechanism — Proof of Work (Bitcoin) uses computing power, Proof of Stake (Ethereum) uses staked coins.",
      },
      {
        heading: "Step 5: Chain Addition",
        text: "The validated block is added to the chain. Every node updates their copy. The transaction is now permanent and irreversible.",
      },
      {
        heading: "Step 6: Complete",
        text: "The recipient now has the funds. The entire process takes seconds to minutes depending on the blockchain.",
      },
    ],
    keyPoints: [
      "Transaction → Verification → Block → Consensus → Chain",
      "Proof of Work: Miners solve math puzzles (Bitcoin)",
      "Proof of Stake: Validators lock up coins as collateral (Ethereum)",
      "Every node holds a complete copy of the ledger",
      "Process is automatic — no human intervention needed",
    ],
  },
  {
    id: "types",
    title: "Types of Blockchains",
    icon: Globe,
    color: "text-violet-400",
    content: [
      {
        heading: "Public Blockchains",
        text: "Open to anyone. No permission needed. Examples: Bitcoin, Ethereum, Solana. Anyone can read, write, and validate transactions.",
      },
      {
        heading: "Private Blockchains",
        text: "Controlled by one organization. Used by businesses for internal operations. Faster but less decentralized.",
      },
      {
        heading: "Layer 1 vs Layer 2",
        text: "Layer 1 is the base blockchain (Bitcoin, Ethereum). Layer 2 is built on top to make it faster and cheaper (Lightning Network, Arbitrum, Base).",
      },
    ],
    keyPoints: [
      "Public: Open, permissionless, fully decentralized",
      "Private: Restricted access, faster, enterprise-focused",
      "Layer 1: Base chain (ETH, BTC, SOL)",
      "Layer 2: Speed/cost upgrades built on top (Arbitrum, Base)",
    ],
  },
  {
    id: "realworld",
    title: "Real-World Uses",
    icon: Database,
    color: "text-cyan-400",
    content: [
      {
        heading: "Money & Payments",
        text: "Send money globally in seconds for pennies. No bank hours, no wire fees, no 3-day delays. Bitcoin processes $10B+ daily.",
      },
      {
        heading: "DeFi (Decentralized Finance)",
        text: "Lend, borrow, trade, and earn interest without banks. Over $50B locked in DeFi protocols. Available 24/7 worldwide.",
      },
      {
        heading: "Supply Chain",
        text: "Track products from factory to store shelf. Walmart uses blockchain to trace food contamination from days to seconds.",
      },
      {
        heading: "Identity & Voting",
        text: "Secure digital identity without passwords. Tamper-proof voting systems. You control your own data, not corporations.",
      },
      {
        heading: "NFTs & Ownership",
        text: "Prove ownership of digital or physical assets on-chain. Art, music, real estate deeds, event tickets — all verifiable.",
      },
    ],
    keyPoints: [
      "Global payments: Fast, cheap, borderless",
      "DeFi: Banking without banks ($50B+ TVL)",
      "Supply chain: End-to-end product tracking",
      "Digital identity: Self-sovereign, no passwords",
      "NFTs: Provable ownership of any asset",
    ],
  },
];

function Section({ section, isExpanded, onToggle }) {
  const Icon = section.icon;
  return (
    <div className="border border-amber-500/15 rounded-sm overflow-hidden" data-testid={`bc-section-${section.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-900/40 hover:bg-zinc-900/60 transition-colors text-left"
      >
        <Icon size={18} weight="bold" className={section.color} />
        <span className="text-sm font-semibold text-zinc-100 flex-1">{section.title}</span>
        {isExpanded ? <CaretDown size={14} className="text-zinc-500" /> : <CaretRight size={14} className="text-zinc-500" />}
      </button>

      {isExpanded && (
        <div className="px-4 py-4 space-y-4 bg-zinc-950/30">
          {section.content.map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="mt-1 shrink-0">
                <ArrowRight size={10} className={section.color} />
              </div>
              <div>
                <h4 className="text-[12px] font-semibold text-zinc-200 mb-0.5">{item.heading}</h4>
                <p className="text-[11px] text-zinc-400 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}

          {/* Key Points */}
          <div className="mt-3 pt-3 border-t border-zinc-800/40">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-medium block mb-2">Key Takeaways</span>
            <div className="space-y-1.5">
              {section.keyPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle size={11} weight="fill" className="text-amber-500/60 shrink-0 mt-0.5" />
                  <span className="text-[11px] text-zinc-300">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BlockchainEducation() {
  const [expanded, setExpanded] = useState(new Set(["what"]));

  const toggle = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  return (
    <div className="p-5 max-w-[900px] space-y-4" data-testid="blockchain-education">
      <div className="mb-4">
        <div className="flex items-center gap-2.5 mb-1">
          <Cube size={20} weight="bold" className="text-amber-400" />
          <h2 className="font-heading text-xl font-bold text-zinc-50 tracking-tight">Blockchain 101</h2>
        </div>
        <p className="text-xs text-zinc-500 ml-[30px]">What it is, why it matters, and how it works — explained simply</p>
      </div>

      {/* Quick nav */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => { if (!expanded.has(s.id)) toggle(s.id); document.getElementById(`bc-nav-${s.id}`)?.scrollIntoView({ behavior: "smooth" }); }}
            className="text-[10px] px-2 py-1 rounded-sm border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {SECTIONS.map(s => (
          <div key={s.id} id={`bc-nav-${s.id}`}>
            <Section section={s} isExpanded={expanded.has(s.id)} onToggle={() => toggle(s.id)} />
          </div>
        ))}
      </div>

      <div className="mt-4 p-4 bg-zinc-900/30 border border-zinc-800/50 rounded-sm">
        <p className="text-[10px] text-zinc-600 leading-relaxed">
          <ShieldCheck size={12} className="inline mr-1 text-zinc-500" />
          <strong className="text-zinc-500">Note:</strong> This is educational content to help you understand blockchain technology. It is not investment advice. Always research thoroughly before making financial decisions.
        </p>
      </div>
    </div>
  );
}
